import { Anchor, Badge, Button, Card, Flex, Grid, Text, Tooltip } from '@mantine/core';
import { IconCalendarOff, IconChartLine, IconHistory, IconSettings, IconUsers } from '@tabler/icons-react';
import { useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router';
import HeaderStatistic from '../../../components/HeaderStatistic';
import LazyLoadSpinner from '../../../components/LazyLoaderSpinner';
import ListingPage from '../../../components/ListingPage';
import prettifiedLabel from '../../../constants/labelPrettifier';

const BannerCard = ({ banner, stats }) => {
  const location = useLocation();
  const { accountId, domainId } = useParams();
  const currentDomain = useSelector((state) => state.domain.domain);
  const domainCurrency = currentDomain.currency;

  const linkToSegment = banner.segment
    ? `#/accounts/${accountId}/domains/${domainId}/segments/${banner.segment.id}`
    : null;

  const linkToProfileFilterSegment = banner.realtimeSegment
    ? `#/accounts/${accountId}/domains/${domainId}/segments/${banner.realtimeSegment.id}`
    : null;

  const isExpired = banner.endTime && new Date(banner.endTime).getTime() < new Date().getTime();

  let statsElements;

  if (!stats) {
    statsElements = (
      <Grid.Col span={12}>
        <Grid justify="center">
          <LazyLoadSpinner />
        </Grid>
      </Grid.Col>
    );
  } else {
    statsElements = (
      <>
        <Grid.Col span={2} ta="center">
          <HeaderStatistic title="Impressions" stats={stats} stat="impressions" id={banner.id} statKey="count" />
        </Grid.Col>
        <Grid.Col span={2} ta="center">
          <HeaderStatistic
            title="Closes"
            stats={stats}
            stat="closes"
            id={banner.id}
            percentageOfStat="impressions"
            statKey="count"
          />
        </Grid.Col>
        <Grid.Col span={2} ta="center">
          <HeaderStatistic
            title="Clicks"
            stats={stats}
            stat="clicks"
            id={banner.id}
            percentageOfStat="impressions"
            statKey="count"
          />
        </Grid.Col>
        <Grid.Col span={2} ta="center">
          <HeaderStatistic
            title="Conversions"
            stats={stats}
            stat="conversions"
            percentageOfStat="clicks"
            id={banner.id}
            statKey="count"
          />
        </Grid.Col>
        <Grid.Col span={2} ta="center">
          <HeaderStatistic
            title="Purchases"
            stats={stats}
            stat="purchases"
            percentageOfStat="conversions"
            id={banner.id}
            statKey="count"
          />
        </Grid.Col>
        <Grid.Col span={2} ta="center">
          <HeaderStatistic
            title="Revenue"
            stats={stats}
            stat="purchases"
            id={banner.id}
            statKey="value"
            currency={domainCurrency}
          />
        </Grid.Col>
      </>
    );
  }

  return (
    <Card mb="md" data-tour={`bannersName=${banner.name}`} withBorder>
      <Card.Section inheritPadding py="sm">
        <Grid>
          <Grid.Col span={{ base: 12, sm: 8 }}>
            <Flex align="center" gap="sm" mb="xs">
              <Anchor href={`#${location.pathname}/${banner.id}/details`} fw={600}>
                {banner.name}
              </Anchor>
              {isExpired && (
                <Text c="dimmed" size="sm">
                  <IconCalendarOff size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                  Expired
                </Text>
              )}
            </Flex>
            <Flex gap="xs" wrap="wrap">
              <Badge variant="default">
                <IconUsers size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                <Anchor href={linkToSegment} target="_blank" size="xs">
                  {banner.segment?.name || 'Unknown'} {banner.inverseSegmentSelection ? '(exclude)' : ''}
                </Anchor>
              </Badge>
              {banner.realtimeSegment && (
                <Badge variant="default">
                  <IconUsers size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                  <Anchor href={linkToProfileFilterSegment} target="_blank" size="xs">
                    {banner.realtimeSegment?.name || 'Unknown'}
                  </Anchor>
                </Badge>
              )}
              <Badge variant="default">
                <IconSettings size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                {prettifiedLabel(banner.type)}
              </Badge>
              <Tooltip
                label={
                  <>
                    <div>Created: {new Date(banner.createdAt).toLocaleString()}</div>
                    <div>Updated: {new Date(banner.updatedAt).toLocaleString()}</div>
                  </>
                }
                position="top"
                withArrow
              >
                <Badge variant="default" style={{ cursor: 'pointer' }}>
                  <IconHistory size={14} style={{ verticalAlign: 'middle' }} />
                </Badge>
              </Tooltip>
            </Flex>
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 4 }}>
            <Flex justify="flex-end" align="flex-start">
              <Button component="a" href={`#${location.pathname}/${banner.id}/analytics`} size="xs">
                Statistics <IconChartLine size={14} style={{ marginLeft: 4 }} />
              </Button>
            </Flex>
          </Grid.Col>
        </Grid>
      </Card.Section>
      <Card.Section inheritPadding py="sm">
        <Grid>{statsElements}</Grid>
      </Card.Section>
    </Card>
  );
};

const BannerCards = ({ banners, stats }) => {
  return banners.map((b, i) => <BannerCard key={i} banner={b} stats={stats} />);
};

const BannersPage = () => {
  return (
    <ListingPage
      entityType="banner"
      useStats={true}
      filterBy="name"
      Cards={BannerCards}
      createRestrictionKey="bannersCreate"
    />
  );
};

export default BannersPage;
